import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Clock, ArrowLeft, Plus, Play, Pause, Edit, 
  BarChart, CheckCircle, AlertTriangle, Settings,
  MoreHorizontal, Trash2, Calendar, Users
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const EmailAutomation = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [activeTab, setActiveTab] = useState("active");
  
  // Mock data
  const automations = [
    {
      id: 1,
      name: "Abandoned Registration Recovery",
      status: "Active",
      trigger: "Registration abandoned",
      emails: 3,
      lastTriggered: "Today",
      recipients: 28,
      conversionRate: "24%",
      revenue: "$1,820",
    },
    {
      id: 2,
      name: "Welcome Series",
      status: "Active",
      trigger: "New account",
      emails: 4,
      lastTriggered: "Yesterday",
      recipients: 156,
      conversionRate: "38%",
      revenue: "$4,250",
    },
    {
      id: 3,
      name: "Post-Event Follow-up",
      status: "Inactive",
      trigger: "Event completion",
      emails: 2,
      lastTriggered: "Jul 15, 2023",
      recipients: 0,
    },
    {
      id: 4,
      name: "Membership Renewal",
      status: "Draft",
      trigger: "30 days before expiration",
      emails: 3,
      lastTriggered: "Never",
      recipients: 0,
    },
  ];

  const tabs = [
    { id: "active", label: "Active", count: 2 },
    { id: "inactive", label: "Inactive", count: 1 },
    { id: "drafts", label: "Drafts", count: 1 },
  ];

  const getFilteredAutomations = () => {
    if (activeTab === "active") {
      return automations.filter(a => a.status === "Active");
    } else if (activeTab === "inactive") {
      return automations.filter(a => a.status === "Inactive");
    } else {
      return automations.filter(a => a.status === "Draft");
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/admin/marketing")}
            className="btn btn-circle btn-ghost"
            aria-label="Back to Marketing Hub"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Clock size={24} className="text-primary" />
              Email Automation
            </h1>
            <p className="text-base-content/60">
              Create automated email sequences to recover abandoned registrations and reduce churn
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={() => navigate("/admin/marketing/automation/new")}
            className="btn btn-primary"
          >
            <Plus size={16} />
            New Automation
          </button>
        </div>
      </div>

      {/* Pre-built templates callout */}
      <div className="alert shadow-sm">
        <CheckCircle className="text-success" />
        <div>
          <h3 className="font-bold">Pre-built automation templates available</h3>
          <div className="text-sm">
            Get started quickly with our pre-built automation templates for abandoned carts, welcome series, and more.
          </div>
        </div>
        <button className="btn btn-sm">View Templates</button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`px-4 py-2 font-medium ${
              activeTab === tab.id
                ? "border-b-2 border-primary text-primary"
                : "text-base-content/60"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-base-200">
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Main content area */}
      <div className={`${
        isDarkMode
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5'
          : 'bg-base-100'
      } rounded-box shadow-sm overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>Automation Name</th>
                <th>Trigger</th>
                <th>Emails</th>
                <th>Last Triggered</th>
                <th>Recipients</th>
                <th>Performance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredAutomations().map((automation) => (
                <tr key={automation.id} className="hover:bg-base-200">
                  <td className="font-medium" onClick={() => navigate(`/admin/marketing/automation/${automation.id}`)}>
                    <div className="flex items-center gap-3 cursor-pointer">
                      <div className={`w-2 h-2 rounded-full ${
                        automation.status === "Active" 
                          ? "bg-success" 
                          : automation.status === "Draft" 
                          ? "bg-secondary" 
                          : "bg-base-content/30"
                      }`} />
                      {automation.name}
                    </div>
                  </td>
                  <td>{automation.trigger}</td>
                  <td className="text-center">{automation.emails}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {automation.lastTriggered}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Users size={14} />
                      {automation.recipients}
                    </div>
                  </td>
                  <td>
                    {automation.status === "Active" ? (
                      <div className="flex flex-col">
                        <span>Conversion: {automation.conversionRate}</span>
                        <span>Revenue: {automation.revenue}</span>
                      </div>
                    ) : (
                      <span className="text-base-content/60">—</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {automation.status === "Active" ? (
                        <button className="btn btn-ghost btn-xs" title="Pause">
                          <Pause size={14} />
                        </button>
                      ) : (
                        <button className="btn btn-ghost btn-xs text-success" title="Start">
                          <Play size={14} />
                        </button>
                      )}
                      <button className="btn btn-ghost btn-xs" title="Edit">
                        <Edit size={14} />
                      </button>
                      <button className="btn btn-ghost btn-xs" title="Stats">
                        <BarChart size={14} />
                      </button>
                      <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-ghost btn-xs">
                          <MoreHorizontal size={14} />
                        </label>
                        <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
                          <li><a>Duplicate</a></li>
                          <li><a>Settings</a></li>
                          <li><a className="text-error">Delete</a></li>
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Featured automation template */}
      <div className={`${
        isDarkMode
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5'
          : 'bg-base-200'
      } shadow-sm rounded-box card-body`}>
        <div className="flex justify-between items-start">
          <div>
            <h2 className="card-title flex items-center gap-2">
              <AlertTriangle className="text-warning" size={20} />
              Recommended Automation
            </h2>
            <p className="text-base-content/70 mt-1">
              Increase your conversion rate with our abandoned registration recovery sequence
            </p>
          </div>
          <button className="btn btn-primary btn-sm">Create from Template</button>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`${
            isDarkMode
              ? 'bg-[#131620] border border-white/5'
              : 'bg-base-100'
          } shadow-sm rounded-box`}>
            <div className="card-body p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-sm">Email 1</h3>
                <span className="badge badge-sm">1 hour</span>
              </div>
              <p className="text-xs text-base-content/70">
                "Did you forget something?" - Gentle reminder with saved progress
              </p>
            </div>
          </div>
          <div className={`${
            isDarkMode
              ? 'bg-[#131620] border border-white/5'
              : 'bg-base-100'
          } shadow-sm rounded-box`}>
            <div className="card-body p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-sm">Email 2</h3>
                <span className="badge badge-sm">24 hours</span>
              </div>
              <p className="text-xs text-base-content/70">
                "Don't miss out!" - Highlight benefits and offer assistance
              </p>
            </div>
          </div>
          <div className={`${
            isDarkMode
              ? 'bg-[#131620] border border-white/5'
              : 'bg-base-100'
          } shadow-sm rounded-box`}>
            <div className="card-body p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-sm">Email 3</h3>
                <span className="badge badge-sm">3 days</span>
              </div>
              <p className="text-xs text-base-content/70">
                "Limited spots remaining" - Create urgency with capacity details
              </p>
            </div>
          </div>
          <div className={`${
            isDarkMode
              ? 'bg-[#131620] border border-white/5'
              : 'bg-base-100'
          } shadow-sm rounded-box`}>
            <div className="card-body p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-sm">Email 4</h3>
                <span className="badge badge-sm">5 days</span>
              </div>
              <p className="text-xs text-base-content/70">
                "Final reminder" - Last chance with potential incentive offer
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Automation tips */}
      <div className="alert bg-primary/10">
        <Settings className="text-primary" />
        <div>
          <h3 className="font-bold">Automation best practices</h3>
          <div className="text-sm">
            <ul className="mt-1 space-y-1">
              <li>• Send the first recovery email within 1 hour of abandonment</li>
              <li>• Use personalization to increase engagement</li>
              <li>• Include clear calls to action in each email</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailAutomation; 